import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  AspectRatio,
  AdminGenerationMenuItem,
  AdminGenerationMenuCategoryItem,
  MenuExecutionFormData,
  User,
  CarInfo,
  CarSettings,
  AllReferencesResponse,
} from '../../types';
import { AVAILABLE_ASPECT_RATIOS } from '../../constants';
import { ImageUpload, ImageUploadRef } from '@/components/ImageUpload';
import { SampleImagesModal } from '../modals/SampleImagesModal';
import { TermsModal } from '../modals/TermsModal';
import {
  SparklesIcon,
  AdjustmentsHorizontalIcon,
  BoltIcon,
  DocumentTextIcon,
  PhotoIcon as CameraIcon,
  ListBulletIcon,
  BeakerIcon,
  EyeIcon,
} from '../icons/HeroIcons';
import { useCategories } from '@/contexts/CategoriesContext';
import { useMenus } from '@/contexts/MenusContext';
import { useCredits } from '@/contexts/CreditsContext';
import { getCarInfo } from '@/services/api/car-info';
import { fetchCarSettings } from '@/services/api/car-settings';
import { checkPhoneUserExists } from '@/services/api/phone-login';
import { sanitizeInput, sanitizeTextArea } from '@/utils/sanitize';
import { fetchAllReferences } from '@/services/api/user-references';

// 愛車参照キーの定義
const CAR_REFERENCE_KEYS = [
  'car_front',
  'car_side',
  'car_rear',
  'car_front_angled_7_3',
  'car_rear_angled_7_3',
  'car_number',
  'car_numbertext',
  'car_nickname',
] as const;

interface MenuExecutionPanelProps {
  onGenerateClick: (formData: MenuExecutionFormData) => void;
  isGenerating: boolean;
  formData: MenuExecutionFormData;
  setFormData: React.Dispatch<React.SetStateAction<MenuExecutionFormData>>;
  currentUser: User | null;
  isMenuLocked?: boolean; // メニュー固定モード（カテゴリ・メニュー選択を非表示）
}

export const MenuExecutionPanel: React.FC<MenuExecutionPanelProps> = ({
  onGenerateClick,
  isGenerating,
  formData,
  setFormData,
  currentUser,
  isMenuLocked = false,
}) => {
  const categories = useCategories();
  const menus = useMenus();
  const credits = useCredits();

  // メニュー一覧が更新されたら、選択中メニューを最新データに同期
  // （管理画面でメニュー編集後に古い参照設定を見続けないように）
  useEffect(() => {
    if (formData.menu) {
      const latest = menus.find((m) => m.id === formData.menu!.id);
      if (latest && latest !== formData.menu) {
        setFormData((prev) => ({ ...prev, menu: latest }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus]);

  const imageUploadRef = useRef<ImageUploadRef>(null);
  const [showSampleModal, setShowSampleModal] = useState(false);
  
  // MyGarageから選択機能の状態管理
  const [isCarSelectModalOpen, setIsCarSelectModalOpen] = useState(false);
  const [carList, setCarList] = useState<CarInfo[]>([]);
  const [selectedCar, setSelectedCar] = useState<CarInfo | null>(null);
  const [hasPhoneUser, setHasPhoneUser] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // 参照設定データ
  const [userReferences, setUserReferences] = useState<AllReferencesResponse | null>(null);

  // 愛車設定データ（選択した車両の参照画像用）
  const [selectedCarSettings, setSelectedCarSettings] = useState<CarSettings | null>(null);

  // 参照設定キーから画像URLを取得するヘルパー関数（API用）
  const getImageUrlFromRefKey = useCallback((refKey: string): string | null => {
    if (!userReferences || !refKey.startsWith('ref_')) return null;

    // reference_keyフィールドで検索する
    // ref_self1, ref_self2, ref_self3
    if (refKey.startsWith('ref_self')) {
      const selfRef = userReferences.self_references.find(r => r.reference_key === refKey);
      return selfRef?.photo_url || null;
    }

    // ref_family1, ref_family2, ...
    if (refKey.startsWith('ref_family')) {
      const familyRef = userReferences.family_references.find(r => r.reference_key === refKey);
      return familyRef?.photo_url || null;
    }

    // ref_garage1, ref_garage2, ...
    if (refKey.startsWith('ref_garage')) {
      const garageRef = userReferences.garage_references?.find(r => r.reference_key === refKey);
      return garageRef?.photo_url || null;
    }

    // ref_other1, ref_other2, ...
    if (refKey.startsWith('ref_other')) {
      const otherRef = userReferences.other_references.find(r => r.reference_key === refKey);
      return otherRef?.photo_url || null;
    }

    // ref_carTalent1, ref_carTalent2, ... (カータレント)
    if (refKey.startsWith('ref_carTalent')) {
      const staffRef = userReferences.staff_references.find(r => r.reference_key === refKey);
      return staffRef?.photo_url || null;
    }

    // ref_carModel1, ref_carModel2, ... (カーモデル)
    if (refKey.startsWith('ref_carModel')) {
      const carModelRef = userReferences.car_model_references?.find(r => r.reference_key === refKey);
      return carModelRef?.photo_url || null;
    }

    // ref_carSpot1, ref_carSpot2, ... (カースポット)
    if (refKey.startsWith('ref_carSpot')) {
      const carSpotRef = userReferences.car_spot_references?.find(r => r.reference_key === refKey);
      return carSpotRef?.photo_url || null;
    }

    return null;
  }, [userReferences]);

  // 参照設定キーからユーザー表示用画像URLを取得するヘルパー関数
  // カータレント、カーモデル、カースポットの場合はdisplay_image_urlを返す（ネタバレ防止）
  const getDisplayImageUrlFromRefKey = useCallback((refKey: string): string | null => {
    if (!userReferences || !refKey.startsWith('ref_')) return null;

    // ref_carTalent1, ref_carTalent2, ... (カータレント) は表示用画像を返す
    if (refKey.startsWith('ref_carTalent')) {
      const staffRef = userReferences.staff_references.find(r => r.reference_key === refKey);
      // display_image_urlがあればそれを返す、なければphoto_urlを返す
      return staffRef?.display_image_url || staffRef?.photo_url || null;
    }

    // ref_carModel1, ref_carModel2, ... (カーモデル) は表示用画像を返す
    if (refKey.startsWith('ref_carModel')) {
      const carModelRef = userReferences.car_model_references?.find(r => r.reference_key === refKey);
      return carModelRef?.display_image_url || carModelRef?.photo_url || null;
    }

    // ref_carSpot1, ref_carSpot2, ... (カースポット) は表示用画像を返す
    if (refKey.startsWith('ref_carSpot')) {
      const carSpotRef = userReferences.car_spot_references?.find(r => r.reference_key === refKey);
      return carSpotRef?.display_image_url || carSpotRef?.photo_url || null;
    }

    // その他は通常の画像URLを返す
    return getImageUrlFromRefKey(refKey);
  }, [userReferences, getImageUrlFromRefKey]);

  // 参照設定キーから表示名を取得するヘルパー関数
  const getDisplayNameFromRefKey = useCallback((refKey: string): string => {
    if (!userReferences || !refKey.startsWith('ref_')) return refKey;

    // ref_self → 自分の写真（写真タイプ）
    if (refKey.startsWith('ref_self')) {
      const selfRef = userReferences.self_references.find(r => r.reference_key === refKey);
      return selfRef ? `自分の写真（${selfRef.photo_type_display}）` : '自分の写真';
    }

    // ref_family → 家族名
    if (refKey.startsWith('ref_family')) {
      const familyRef = userReferences.family_references.find(r => r.reference_key === refKey);
      return familyRef?.family_name || 'ファミリー';
    }

    // ref_garage → ガレージ名
    if (refKey.startsWith('ref_garage')) {
      const garageRef = userReferences.garage_references?.find(r => r.reference_key === refKey);
      return garageRef?.garage_name || 'ガレージ';
    }

    // ref_other → リファレンス名
    if (refKey.startsWith('ref_other')) {
      const otherRef = userReferences.other_references.find(r => r.reference_key === refKey);
      return otherRef?.reference_name || 'お気に入り';
    }

    // ref_carTalent → タレント名
    if (refKey.startsWith('ref_carTalent')) {
      const staffRef = userReferences.staff_references.find(r => r.reference_key === refKey);
      return staffRef?.staff_name || 'カータレント';
    }

    // ref_carModel → モデル名
    if (refKey.startsWith('ref_carModel')) {
      const carModelRef = userReferences.car_model_references?.find(r => r.reference_key === refKey);
      return carModelRef?.model_name || 'カーモデル';
    }

    // ref_carSpot → スポット名
    if (refKey.startsWith('ref_carSpot')) {
      const carSpotRef = userReferences.car_spot_references?.find(r => r.reference_key === refKey);
      return carSpotRef?.spot_name || 'カースポット';
    }

    return refKey;
  }, [userReferences]);

  // 愛車参照キーから画像URLを取得するヘルパー関数
  const getCarReferenceImageUrl = useCallback((refKey: string): string | null => {
    if (!selectedCarSettings) return null;

    switch (refKey) {
      case 'car_front':
        return selectedCarSettings.car_photo_front_url || null;
      case 'car_side':
        return selectedCarSettings.car_photo_side_url || null;
      case 'car_rear':
        return selectedCarSettings.car_photo_rear_url || null;
      case 'car_front_angled_7_3':
        return selectedCarSettings.car_photo_diagonal_url || null;
      case 'car_rear_angled_7_3':
        return selectedCarSettings.car_photo_rear_diagonal_url || null;
      case 'car_number':
        return selectedCarSettings.original_number_image_url || null;
      default:
        return null;
    }
  }, [selectedCarSettings]);

  // 愛車参照キーからテキスト値を取得するヘルパー関数
  const getCarReferenceText = useCallback((refKey: string): string | null => {
    if (!selectedCarSettings) return null;

    switch (refKey) {
      case 'car_numbertext':
        return selectedCarSettings.license_plate_text || null;
      case 'car_nickname':
        return selectedCarSettings.car_name || null;
      default:
        return null;
    }
  }, [selectedCarSettings]);

  // 愛車参照キーの表示名を取得するヘルパー関数
  const getCarReferenceDisplayName = useCallback((refKey: string): string => {
    switch (refKey) {
      case 'car_front':
        return '愛車（フロント正面）';
      case 'car_side':
        return '愛車（真横）';
      case 'car_rear':
        return '愛車（リア正面）';
      case 'car_front_angled_7_3':
        return '愛車（斜め前7:3）';
      case 'car_rear_angled_7_3':
        return '愛車（斜め後ろ7:3）';
      case 'car_number':
        return '愛車（ナンバー画像）';
      case 'car_numbertext':
        return '愛車（ナンバーテキスト）';
      case 'car_nickname':
        return '愛車（ニックネーム）';
      default:
        return refKey;
    }
  }, []);

  // メニュー固定参照キーかどうかを判定
  const isMenuReference = useCallback((label: string): boolean => {
    return label.startsWith('menu_ref_');
  }, []);

  // メニュー固定参照キーから画像URLを取得するヘルパー関数
  const getMenuRefImageUrl = useCallback((label: string): string | null => {
    if (!formData.menu || !label.startsWith('menu_ref_')) return null;

    const menuRefImages = formData.menu.menuReferenceImages || formData.menu.menu_reference_images || [];
    if (menuRefImages.length === 0) return null;

    // menu_ref_N からインデックスを抽出（1-based to 0-based）
    try {
      const index = parseInt(label.replace('menu_ref_', ''), 10) - 1;
      if (index >= 0 && index < menuRefImages.length) {
        return menuRefImages[index].url || null;
      }
    } catch {
      return null;
    }
    return null;
  }, [formData.menu]);

  // メニュー固定参照キーからラベル名を取得するヘルパー関数
  const getMenuRefLabel = useCallback((label: string): string => {
    if (!formData.menu || !label.startsWith('menu_ref_')) return label;

    const menuRefImages = formData.menu.menuReferenceImages || formData.menu.menu_reference_images || [];
    if (menuRefImages.length === 0) return label;

    try {
      const index = parseInt(label.replace('menu_ref_', ''), 10) - 1;
      if (index >= 0 && index < menuRefImages.length) {
        return menuRefImages[index].label || label;
      }
    } catch {
      return label;
    }
    return label;
  }, [formData.menu]);

  // メニューに愛車参照キーが含まれているかチェック（プロンプト内のテキスト参照タグも含む）
  const hasCarReferenceKeys = useCallback((): boolean => {
    const refLabels = formData.menu?.refImageLabels || formData.menu?.ref_image_labels || [];
    const hasImageRef = refLabels.some(label => CAR_REFERENCE_KEYS.includes(label as typeof CAR_REFERENCE_KEYS[number]));
    // プロンプト内のテキスト参照タグもチェック
    const prompt = formData.menu?.prompt || '';
    const hasTextRef = prompt.includes('{car_numbertext}') || prompt.includes('{car_nickname}');
    return hasImageRef || hasTextRef;
  }, [formData.menu]);

  useEffect(() => {
    let initialCategory: AdminGenerationMenuCategoryItem | null = null;

    if (!formData.category) {
      initialCategory =
        categories && 0 < categories.length ? categories[0] : null;
      setFormData((prev) => ({
        ...prev,
        category: initialCategory,
      }));
    }

    if (!formData.menu) {
      const initialMenu =
        initialCategory && menus
          ? (menus.find((m) => m.categoryId === initialCategory.id && !(m.isHidden ?? m.is_hidden)) ?? null)
          : null;
      setFormData((prev) => ({
        ...prev,
        menu: initialMenu,
      }));
    }

    // 画像アップロードの場合は画像比率を「元画像のまま」に設定
    // ただし、メニューにデフォルトのアスペクト比がある場合はそちらを優先
    const menuDefaultAspectRatio = formData.menu?.defaultAspectRatio || formData.menu?.default_aspect_ratio;
    if (formData.inputType === 'upload' && !menuDefaultAspectRatio && formData.aspectRatio !== '') {
      setFormData((prev) => ({
        ...prev,
        aspectRatio: '',
      }));
    }
  }, [formData.inputType]); // inputTypeの変更のみ監視

  // メニューが変更されたときにデフォルトのアスペクト比を適用（初期読み込み・URL経由の場合も含む）
  useEffect(() => {
    if (!formData.menu) return;

    const menuDefaultAspectRatio = formData.menu.defaultAspectRatio || formData.menu.default_aspect_ratio;
    if (menuDefaultAspectRatio && formData.aspectRatio !== menuDefaultAspectRatio) {
      setFormData((prev) => ({
        ...prev,
        aspectRatio: menuDefaultAspectRatio,
      }));
    }
  }, [formData.menu?.id]); // メニューIDの変更のみ監視

  // 現在のユーザーがphone_usersテーブルに存在するかのチェック
  useEffect(() => {
    const checkPhoneUser = async () => {
      if (!currentUser?.id) return;
      
      try {
        const result = await checkPhoneUserExists(currentUser.id);
        setHasPhoneUser(result.hasPhoneUser);
      } catch (error) {
        console.error('Phone user check error:', error);
        setHasPhoneUser(false);
      }
    };
    
    checkPhoneUser();
  }, [currentUser?.id]);

  // ユーザーの参照設定を取得
  useEffect(() => {
    const loadUserReferences = async () => {
      if (!currentUser?.id) return;

      try {
        const refs = await fetchAllReferences(currentUser.id);
        setUserReferences(refs);
      } catch (error) {
        console.error('Failed to load user references:', error);
      }
    };

    loadUserReferences();
  }, [currentUser?.id]);

  // メニュー変更時に参照設定キーの画像を自動セット
  useEffect(() => {
    const autoSetReferenceImages = async () => {
      console.log('🔄 autoSetReferenceImages called', { menuId: formData.menu?.id, hasUserRefs: !!userReferences });
      if (!formData.menu || !userReferences) return;

      const refLabels = formData.menu.refImageLabels || formData.menu.ref_image_labels || [];
      console.log('📋 refLabels:', refLabels);

      // refLabelsが空の場合、既存の参照画像をクリア
      if (refLabels.length === 0) {
        if (formData.referenceImages && formData.referenceImages.length > 0) {
          console.log('🧹 refLabelsが空なので参照画像をクリア');
          setFormData((prev) => ({
            ...prev,
            referenceImages: [],
          }));
        }
        return;
      }

      // 各ラベルについて、ref_で始まる場合は画像URLからFileを作成
      // menu_ref_* はバックエンドで自動解決されるのでスキップ
      const newRefImages: (File | null)[] = [];

      for (let i = 0; i < refLabels.length; i++) {
        const label = refLabels[i];
        // menu_ref_* はバックエンドで自動解決されるのでnullのまま
        if (label.startsWith('menu_ref_')) {
          console.log(`⏭️ ${label} -> skipped (menu fixed reference, resolved on backend)`);
          newRefImages[i] = null;
          continue;
        }
        if (label.startsWith('ref_')) {
          const imageUrl = getImageUrlFromRefKey(label);
          console.log(`🖼️ ${label} -> imageUrl:`, imageUrl);
          if (imageUrl) {
            try {
              // URLから画像を取得してFileオブジェクトに変換
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              const file = new File([blob], `${label}.jpg`, { type: blob.type || 'image/jpeg' });
              newRefImages[i] = file;
              console.log(`✅ ${label} -> File created:`, file.name, file.size);
            } catch (error) {
              console.error(`❌ Failed to fetch image for ${label}:`, error);
              newRefImages[i] = null;
            }
          } else {
            console.log(`⚠️ ${label} -> No image URL found in user references`);
            newRefImages[i] = null;
          }
        } else {
          // ref_で始まらない場合は既存の値を維持
          newRefImages[i] = formData.referenceImages?.[i] || null;
        }
      }

      // 変更があった場合のみ更新
      const currentRefs = formData.referenceImages || [];
      const hasChanges = newRefImages.some((img, idx) => {
        const currentImg = currentRefs[idx];
        const label = refLabels[idx];
        // menu_ref_* はスキップ
        if (label?.startsWith('menu_ref_')) {
          return false;
        }
        // ref_キーの場合のみ更新をチェック
        if (label?.startsWith('ref_')) {
          return img !== currentImg;
        }
        return false;
      });

      console.log('🔍 hasChanges:', hasChanges, 'newRefImages:', newRefImages);
      if (hasChanges || newRefImages.length !== currentRefs.length) {
        console.log('📝 Updating referenceImages in formData');
        setFormData((prev) => ({
          ...prev,
          referenceImages: newRefImages,
        }));
      }
    };

    autoSetReferenceImages();
  }, [formData.menu?.id, userReferences, getImageUrlFromRefKey]);

  const handleCategoryChange = (categoryId: number) => {
    const newMenu = menus.find((menu) => menu.categoryId === categoryId && !(menu.isHidden ?? menu.is_hidden)) ?? null;
    const menuDefaultAspectRatio = newMenu?.defaultAspectRatio || newMenu?.default_aspect_ratio;
    setFormData((prev) => ({
      ...prev,
      category: categories.find((c) => c.id === categoryId) ?? null,
      menu: newMenu,
      promptVariables: [],
      // メニューにデフォルトのアスペクト比がある場合は適用
      aspectRatio: menuDefaultAspectRatio || prev.aspectRatio,
    }));
  };

  const handleMenuChange = (menuId: number) => {
    const newMenu = menus.find((menu) => menu.id === menuId) ?? null;
    const menuDefaultAspectRatio = newMenu?.defaultAspectRatio || newMenu?.default_aspect_ratio;
    setFormData((prev) => ({
      ...prev,
      menu: newMenu,
      promptVariables: [],
      // メニューにデフォルトのアスペクト比がある場合は適用
      aspectRatio: menuDefaultAspectRatio || prev.aspectRatio,
    }));
  };

  const handleImageSelectedByUploader = useCallback((file: File | null) => {
    setFormData((prev) => ({
      ...prev,
      image: file,
      // 画像をクリアしてもアップロードモードを維持
      // inputType: file ? 'upload' : 'prompt', // 削除
    }));
  }, []);

  const handleAdditionalPromptForMyCar = useCallback((text: string) => {
    const sanitizedText = sanitizeTextArea(text);
    setFormData((prev) => ({
      ...prev,
      additionalPromptForMyCar: sanitizedText,
    }));
  }, []);

  const handleAdditionalPromptForOthers = useCallback((text: string) => {
    setFormData((prev) => ({
      ...prev,
      additionalPromptForOthers: text,
    }));
  }, []);

  const handleAspectRatio = useCallback((aspectRatio: AspectRatio) => {
    setFormData((prev) => ({
      ...prev,
      aspectRatio: aspectRatio,
    }));
  }, []);

  const handlePromptVariablesChange = (key: string, value: string, multiline?: boolean) => {
    const sanitizedValue = multiline ? sanitizeTextArea(value) : sanitizeInput(value);
    setFormData((prev) => {
      const updated = prev.promptVariables.some((p) => p.key === key)
        ? prev.promptVariables.map((p) => (p.key === key ? { ...p, value: sanitizedValue } : p)) // 既存のkeyを更新
        : [...prev.promptVariables, { key, value: sanitizedValue }]; // 新しいkeyを追加

      return {
        ...prev,
        promptVariables: updated,
      };
    });
  };

  const handleInputTypeChange = (inputType: 'prompt' | 'upload') => {
    setFormData((prev) => ({
      ...prev,
      inputType: inputType,
      // 画像アップロードの時は画像比率を「元画像のまま」（空文字列）に設定
      aspectRatio: inputType === 'upload' ? '' : prev.aspectRatio,
    }));
  };

  const selectedMenuCredit = formData.menu?.credit ?? 0;

  // 高度編集エンジンかどうかを判定（編集画像・参照画像対応）
  const isAdvancedEditEngine = ['black_forest_labs2_pro', 'black_forest_labs2_flex', 'nanobanana', 'nanobanana_pro', 'nanobanana2', 'grok_imagen', 'seedream4', 'seedream45', 'seedream5', 'gpt_image', 'gpt_image2', 'gpt_image25_sunburst', 'gpt_image25_flare', 'hotgen_image_v4_pro', 'hotgen_image_v51_pro', 'flux3_image', 'grok_imagine2', 'nanobanana2_lite', 'seedance2', 'seedance2_fast', 'seedance25', 'kling3_omni', 'hotgen_video_v4_pro', 'hotgen_video_v5', 'seedance2_sousaku_refs', 'seedance2_sousaku_i2v', 'seedance25_sousaku_refs', 'seedance25_sousaku_i2v', 'minimax_h3_max_turbo', 'runway_gen45', 'gemini_omni_flash', 'topview_t1_omni', 'seedance25_topview_omni', 'seedance2_topview_omni', 'topview_t1_i2v', 'seedance25_topview_i2v', 'seedance2_topview_i2v'].includes(formData.menu?.engine || '');
  // fal系動画エンジン（start/end画像は任意・画像なしならtext-to-video）
  const isFalVideoEngine = ['minimax_h3_max_turbo', 'runway_gen45', 'gemini_omni_flash'].includes(formData.menu?.engine || '');
  // エンドフレーム非対応（Runway Gen-4.5 / Gemini Omni Flash）
  const isNoEndFrameVideoEngine = ['runway_gen45', 'gemini_omni_flash'].includes(formData.menu?.engine || '');
  // 動画i2v型エンジン（スタート/エンドフレーム指定）
  const isI2vVideoEngine = ['seedance2_sousaku_i2v', 'seedance25_sousaku_i2v', 'hotgen_video_v4_pro', 'topview_t1_i2v', 'seedance25_topview_i2v', 'seedance2_topview_i2v'].includes(formData.menu?.engine || '') || isFalVideoEngine;
  const isHeyGen = formData.menu?.engine === 'heygen';
  const isHeyGenText = isHeyGen && (formData.menu?.heygenMode || 'text') === 'text';
  const isHeyGenAudio = isHeyGen && formData.menu?.heygenMode === 'audio';
  const heygenAllowUserScript = isHeyGenText && !!formData.menu?.heygenAllowUserScript;
  const heygenMaxLen = formData.menu?.heygenUserScriptMaxLength ?? 200;
  const advancedImagesValid = (() => {
    if (!isAdvancedEditEngine) return true;
    const useEditImage = formData.menu?.useEditImage || formData.menu?.use_edit_image;
    const refLabels = formData.menu?.refImageLabels || formData.menu?.ref_image_labels || [];
    const refImages = formData.referenceImages || [];

    console.log('🔍 advancedImagesValid check:', {
      isAdvancedEditEngine,
      useEditImage,
      refLabels,
      refImagesCount: refImages.length,
      refImages: refImages.map((r, i) => r ? `[${i}] ${r.name}` : `[${i}] null`),
      editImage: formData.editImage?.name || null,
    });

    // 編集画像が必要な場合のチェック
    if (useEditImage && !formData.editImage) {
      console.log('❌ editImage required but not set');
      return false;
    }

    // 愛車参照が必要な場合、愛車が選択されているかチェック
    const hasCarImageRef = refLabels.some(label => CAR_REFERENCE_KEYS.includes(label as typeof CAR_REFERENCE_KEYS[number]));
    const prompt = formData.menu?.prompt || '';
    const hasCarTextRef = prompt.includes('{car_numbertext}') || prompt.includes('{car_nickname}');
    if ((hasCarImageRef || hasCarTextRef) && !formData.carId) {
      console.log('❌ Car reference required but no car selected');
      return false;
    }

    // 参照画像が必要な場合のチェック
    if (refLabels.length > 0) {
      // 全ての参照画像がアップロードされているかチェック
      // ただし、menu_ref_* はバックエンドで自動解決されるのでスキップ
      // ref_* で始まるものもユーザー参照として自動解決されるのでスキップ
      for (let i = 0; i < refLabels.length; i++) {
        const label = refLabels[i];
        // menu_ref_* はバックエンドで自動解決
        if (label.startsWith('menu_ref_')) {
          console.log(`⏭️ refImage[${i}] (${label}) skipped - menu fixed reference`);
          continue;
        }
        // ref_* はユーザー参照設定から自動解決
        if (label.startsWith('ref_')) {
          console.log(`⏭️ refImage[${i}] (${label}) skipped - user reference`);
          continue;
        }
        // car_* は愛車参照として自動解決（選択されている場合）
        if (CAR_REFERENCE_KEYS.includes(label as typeof CAR_REFERENCE_KEYS[number])) {
          console.log(`⏭️ refImage[${i}] (${label}) skipped - car reference`);
          continue;
        }
        // ラベルに「任意」が含まれる場合はスキップ（任意の参照画像）
        if (label.includes('任意')) {
          console.log(`⏭️ refImage[${i}] (${label}) skipped - optional label`);
          continue;
        }
        // それ以外（フリー入力ラベル）はアップロードが必要
        if (!refImages[i]) {
          console.log(`❌ refImage[${i}] (${label}) is missing`);
          return false;
        }
      }
    }

    // 高度編集エンジンで編集画像も参照画像もない場合は無効
    // （通常のimage uploadが必要になるが、そのUIは表示されないため）
    if (!useEditImage && refLabels.length === 0) {
      // 通常画像があればOK
      const hasImage = formData.image !== null;
      console.log(`🔍 No editImage/refLabels, checking image: ${hasImage}`);
      return hasImage;
    }

    console.log('✅ advancedImagesValid = true');
    return true;
  })();

  // 愛車参照が必要だが選択されていないかをチェック（エラーメッセージ表示用）
  const carReferenceRequired = (() => {
    if (!isAdvancedEditEngine) return false;
    const refLabels = formData.menu?.refImageLabels || formData.menu?.ref_image_labels || [];
    const hasCarImageRef = refLabels.some(label => CAR_REFERENCE_KEYS.includes(label as typeof CAR_REFERENCE_KEYS[number]));
    const prompt = formData.menu?.prompt || '';
    const hasCarTextRef = prompt.includes('{car_numbertext}') || prompt.includes('{car_nickname}');
    return (hasCarImageRef || hasCarTextRef) && !formData.carId;
  })();

  const canGenerate =
    credits >= selectedMenuCredit &&
    formData.menu &&
    (formData.inputType === 'upload'
      ? isAdvancedEditEngine
        ? advancedImagesValid
        : formData.image !== null
      : formData.additionalPromptForMyCar?.trim() &&
        (!formData.menu.promptVariables ||
          formData.menu.promptVariables.length === 0 ||
          formData.menu.promptVariables.length ===
            formData.promptVariables.length));

  const availableMenus = menus.filter(
    (m) => m.categoryId === formData.category?.id && !(m.isHidden ?? m.is_hidden),
  );

  const handleGeneration = () => {
    onGenerateClick(formData);
  };

  // MyGarageから選択機能のハンドラー
  const handleMyGarageSelect = async () => {
    if (!currentUser?.id) return;
    
    try {
      const cars = await getCarInfo(currentUser.id);
      setCarList(cars);
      setIsCarSelectModalOpen(true);
    } catch (error) {
      console.error('愛車情報取得エラー:', error);
    }
  };

  const handleCarSelect = async (car: CarInfo) => {
    setSelectedCar(car);

    try {
      // 愛車参照キーが使われている場合はCarSettingsを取得
      if (hasCarReferenceKeys() && currentUser?.id) {
        const carSettingsList = await fetchCarSettings(currentUser.id, car.car_id);
        if (carSettingsList && carSettingsList.length > 0) {
          const carSettings = carSettingsList[0];
          setSelectedCarSettings(carSettings);

          // 愛車参照キーの画像をreferenceImagesにセット
          const refLabels = formData.menu?.refImageLabels || formData.menu?.ref_image_labels || [];
          const newRefImages: (File | null)[] = [...(formData.referenceImages || [])];

          for (let i = 0; i < refLabels.length; i++) {
            const label = refLabels[i];
            if (CAR_REFERENCE_KEYS.includes(label as typeof CAR_REFERENCE_KEYS[number])) {
              // 愛車参照キーの場合、画像URLを取得
              let imageUrl: string | null = null;
              switch (label) {
                case 'car_front':
                  imageUrl = carSettings.car_photo_front_url || null;
                  break;
                case 'car_side':
                  imageUrl = carSettings.car_photo_side_url || null;
                  break;
                case 'car_rear':
                  imageUrl = carSettings.car_photo_rear_url || null;
                  break;
                case 'car_front_angled_7_3':
                  imageUrl = carSettings.car_photo_diagonal_url || null;
                  break;
                case 'car_rear_angled_7_3':
                  imageUrl = carSettings.car_photo_rear_diagonal_url || null;
                  break;
                case 'car_number':
                  imageUrl = carSettings.original_number_image_url || null;
                  break;
              }

              if (imageUrl) {
                try {
                  const response = await fetch(imageUrl);
                  const blob = await response.blob();
                  const file = new File([blob], `${label}.jpg`, { type: blob.type || 'image/jpeg' });
                  while (newRefImages.length <= i) {
                    newRefImages.push(null);
                  }
                  newRefImages[i] = file;
                  console.log(`✅ Car ref [${i}] ${label} set from CarSettings`);
                } catch (err) {
                  console.error(`❌ Failed to fetch car ref image ${label}:`, err);
                }
              }
            }
          }

          setFormData((prev) => ({
            ...prev,
            referenceImages: newRefImages,
            carId: car.car_id,  // 愛車IDをセット（テキスト参照用）
          }));
        } else {
          setSelectedCarSettings(null);
          // CarSettingsがなくてもcarIdはセット
          setFormData((prev) => ({
            ...prev,
            carId: car.car_id,
          }));
        }
      } else {
        // 愛車参照キーがない場合でもcarIdはセット
        setFormData((prev) => ({
          ...prev,
          carId: car.car_id,
        }));
      }

      // 通常UIの場合は愛車画像を基準画像としてセット
      if (!isAdvancedEditEngine) {
        const response = await fetch(car.car_image_url);
        const blob = await response.blob();
        const file = new File([blob], `car_${car.id}.jpg`, { type: 'image/jpeg' });
        handleImageSelectedByUploader(file);
      }

      setIsCarSelectModalOpen(false);
    } catch (error) {
      console.error('愛車情報の取得エラー:', error);
      setIsCarSelectModalOpen(false);
    }
  };

  const formatCarName = (car: CarInfo): string => {
    if (car.car_nickname) {
      return `${car.car_brand_en} ${car.car_model_en} [${car.car_nickname}]`;
    }
    return `${car.car_brand_en} ${car.car_model_en}`;
  };

  return (
    <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-xl flex flex-col h-full">
      <div className="flex-grow space-y-4 overflow-y-auto custom-scrollbar pr-1 pb-2">
        {/* メニュー固定モードの場合はメニュー名のみ表示（説明なし） */}
        {isMenuLocked && formData.menu ? (
          <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-lg p-3 border border-indigo-700/30">
            <h3 className="text-lg font-bold text-white flex items-center">
              <SparklesIcon className="w-5 h-5 mr-2 text-indigo-400" />
              {formData.menu.name}
            </h3>
          </div>
        ) : (
          <>
            <div>
              <h3 className="text-md lg:text-lg font-semibold text-indigo-400 mb-2 flex items-center">
                <ListBulletIcon className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
                カテゴリ選択
              </h3>
              <select
                value={formData.category?.id ?? ''}
                onChange={(e) => handleCategoryChange(parseInt(e.target.value, 10))}
                className="w-full p-2.5 text-xs lg:text-sm bg-gray-700 border border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-gray-200"
              >
                <option value="" disabled>
                  カテゴリを選択...
                </option>
                {categories
                  .filter((cat) =>
                    menus.some(
                      (m) =>
                        m.categoryId === cat.id &&
                        !(m.isHidden ?? m.is_hidden),
                    ),
                  )
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </select>
            </div>

            {formData.category && 0 < availableMenus.length && (
              <div>
                <div className="flex justify-between items-center mt-3 mb-2">
                  <h3 className="text-md lg:text-lg font-semibold text-indigo-400 flex items-center">
                    <SparklesIcon className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
                    生成メニュー選択
                  </h3>
                  {formData.menu &&
                    (formData.menu.sampleInputImageUrl ||
                      formData.menu.sampleResultImageUrl) && (
                      <button
                        onClick={() => setShowSampleModal(true)}
                        className="text-xs bg-gray-600 hover:bg-gray-500 text-indigo-300 px-2 py-1 rounded-md flex items-center"
                        title="このメニューの生成サンプルを見る"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />{' '}
                        生成後のサンプルはこちら
                      </button>
                    )}
                </div>
                <select
                  value={formData.menu?.id || ''}
                  onChange={(e) => handleMenuChange(parseInt(e.target.value, 10))}
                  className="w-full p-2.5 text-xs lg:text-sm bg-gray-700 border border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-gray-200"
                >
                  <option value="" disabled>
                    メニューを選択...
                  </option>
                  {availableMenus.map((menu) => (
                    <option key={menu.id} value={menu.id}>
                      {menu.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {formData.menu && (
          <div>
            <div className="mt-3 pt-3 border-t border-gray-700/60 space-y-3">
              <p className="text-sm text-gray-300 italic">
                {formData.menu.description}
              </p>

              {formData.menu.promptVariables &&
                0 < formData.menu.promptVariables.length && (
                  <div>
                    <h4 className="text-sm font-semibold text-indigo-300 mb-1.5">
                      追加入力:
                    </h4>
                    {formData.menu.promptVariables.map((field) => (
                      <div key={field.key} className="mb-2.5">
                        <label
                          htmlFor={field.key}
                          className="block text-xs lg:text-sm font-medium text-gray-300 mb-0.5"
                        >
                          {field.label}
                        </label>
                        {field.multiline ? (
                          <textarea
                            id={field.key}
                            value={
                              formData.promptVariables.find(
                                (v) => v.key === field.key,
                              )?.value || ''
                            }
                            onChange={(e) =>
                              handlePromptVariablesChange(
                                field.key,
                                e.target.value,
                                true,
                              )
                            }
                            placeholder={`${field.label.replace(/^追加入力:\s*/, '')} を入力`}
                            className="w-full p-2 text-xs lg:text-sm bg-gray-700 border border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-gray-200 placeholder-gray-500 resize-y min-h-[80px]"
                            rows={3}
                          />
                        ) : (
                          <input
                            type="text"
                            id={field.key}
                            value={
                              formData.promptVariables.find(
                                (v) => v.key === field.key,
                              )?.value || ''
                            }
                            onChange={(e) =>
                              handlePromptVariablesChange(
                                field.key,
                                e.target.value,
                                false,
                              )
                            }
                            placeholder={`${field.label.replace(/^追加入力:\s*/, '')} を入力`}
                            className="w-full p-2 text-xs lg:text-sm bg-gray-700 border border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-gray-200 placeholder-gray-500"
                            maxLength={10}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

              <div>
                <h3 className="text-md font-semibold text-indigo-400 mb-1 mt-2 flex items-center">
                  <BeakerIcon className="w-5 h-5 mr-1.5" />
                  愛車入力ソース
                </h3>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {(['upload', 'prompt'] as const).map((source) => (
                    <button
                      key={source}
                      onClick={() => handleInputTypeChange(source)}
                      aria-pressed={formData.inputType === source}
                      className={`p-2 rounded-md text-xs font-medium transition-all duration-150 ease-in-out flex items-center justify-center
                        ${formData.inputType === source ? 'bg-indigo-600 text-white ring-1 ring-indigo-400' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}
                    >
                      {source === 'upload' ? (
                        <>
                          <CameraIcon className="w-4 h-4 mr-1" />
                          画像アップロード
                        </>
                      ) : (
                        <>
                          <DocumentTextIcon className="w-4 h-4 mr-1" />
                          テキスト入力ベース
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              {formData.inputType === 'upload' && (
                <>
                  {/* 動画i2v型: スタート/エンドフレーム指定UI */}
                  {isI2vVideoEngine ? (
                    <div className="space-y-3">
                      {/* スタートフレーム */}
                      {(formData.menu?.videoStartFrameMode || formData.menu?.video_start_frame_mode) === 'fixed' && (formData.menu?.videoStartFrameUrl || formData.menu?.video_start_frame_url) ? (
                        <div>
                          <p className="text-sm text-gray-300 mb-1">スタートフレーム（メニュー固定）:</p>
                          <img
                            src={formData.menu?.videoStartFrameUrl || formData.menu?.video_start_frame_url}
                            alt="start frame"
                            className="w-32 h-20 object-cover rounded border border-amber-500"
                          />
                          <p className="text-xs text-gray-400 mt-1">※ 固定のスタートフレームが自動適用されます</p>
                        </div>
                      ) : (
                        <div>
                          <ImageUpload
                            onImageSelect={(file) => {
                              setFormData((prev) => ({
                                ...prev,
                                editImage: file,
                                image: file, // 後方互換性のため
                              }));
                            }}
                            label={isFalVideoEngine ? 'スタートフレーム（動画の開始画像・任意／未指定ならテキストから生成）' : 'スタートフレーム（動画の開始画像・必須）'}
                            uploadedFile={formData.editImage || null}
                            showDeleteButton={false}
                          />
                        </div>
                      )}
                      {/* エンドフレーム */}
                      {!isNoEndFrameVideoEngine && (() => {
                        const endMode = formData.menu?.videoEndFrameMode || formData.menu?.video_end_frame_mode
                          || ((formData.menu?.videoUseEndFrame || formData.menu?.video_use_end_frame) ? 'user' : 'none');
                        const endUrl = formData.menu?.videoEndFrameUrl || formData.menu?.video_end_frame_url;
                        if (endMode === 'fixed' && endUrl) {
                          return (
                            <div>
                              <p className="text-sm text-gray-300 mb-1">エンドフレーム（メニュー固定）:</p>
                              <img src={endUrl} alt="end frame" className="w-32 h-20 object-cover rounded border border-amber-500" />
                              <p className="text-xs text-gray-400 mt-1">※ 固定のエンドフレームが自動適用されます</p>
                            </div>
                          );
                        }
                        if (endMode === 'user') {
                          return (
                            <div>
                              <ImageUpload
                                onImageSelect={(file) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    endImage: file,
                                  }));
                                }}
                                label="エンドフレーム（動画の終了画像・任意）"
                                uploadedFile={formData.endImage || null}
                                showDeleteButton={false}
                              />
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  ) : isAdvancedEditEngine && (formData.menu?.useEditImage || formData.menu?.use_edit_image || (formData.menu?.refImageLabels || formData.menu?.ref_image_labels || []).length > 0) ? (
                    <div className="space-y-3">
                      {/* 編集画像 */}
                      {(formData.menu?.useEditImage || formData.menu?.use_edit_image) && (
                        <div>
                          <ImageUpload
                            onImageSelect={(file) => {
                              setFormData((prev) => ({
                                ...prev,
                                editImage: file,
                                image: file, // 後方互換性のため
                              }));
                            }}
                            label={['seedance2', 'seedance2_fast', 'seedance25', 'kling3_omni', 'seedance2_sousaku_refs', 'seedance25_sousaku_refs', 'hotgen_video_v5', 'topview_t1_omni', 'seedance25_topview_omni', 'seedance2_topview_omni'].includes(formData.menu?.engine || '') ? '参照画像（あなたの画像をアップロード）' : '編集画像（ベースとなる画像）'}
                            uploadedFile={formData.editImage || null}
                            showDeleteButton={false}
                          />
                        </div>
                      )}
                      {/* 参照画像 */}
                      {(() => {
                        const refLabels = formData.menu?.refImageLabels || formData.menu?.ref_image_labels || [];

                        // カータレント等の表示画像を持つ参照を抽出
                        const displayOnlyRefs = refLabels
                          .filter(label => label.startsWith('ref_carTalent') || label.startsWith('ref_carModel') || label.startsWith('ref_carSpot'))
                          .map(label => ({
                            label,
                            displayName: getDisplayNameFromRefKey(label),
                            displayImageUrl: getDisplayImageUrlFromRefKey(label),
                          }))
                          .filter(ref => ref.displayImageUrl);

                        // 愛車参照キーを抽出（ref_image_labelsから）
                        const carRefLabelsFromLabels = refLabels.filter(
                          label => CAR_REFERENCE_KEYS.includes(label as typeof CAR_REFERENCE_KEYS[number])
                        );
                        // プロンプト内のテキスト参照タグも抽出
                        const prompt = formData.menu?.prompt || '';
                        const textRefLabels: string[] = [];
                        if (prompt.includes('{car_numbertext}')) textRefLabels.push('car_numbertext');
                        if (prompt.includes('{car_nickname}')) textRefLabels.push('car_nickname');
                        // 重複を除いて結合
                        const carRefLabels = [...new Set([...carRefLabelsFromLabels, ...textRefLabels])];

                        // メニュー固定参照キーを抽出
                        const menuRefLabels = refLabels.filter(label => isMenuReference(label));

                        // 通常の参照画像（カータレント等以外、愛車参照以外、メニュー固定参照以外、またはフリー入力）
                        const normalRefLabels = refLabels.filter(
                          label => !label.startsWith('ref_carTalent') && !label.startsWith('ref_carModel') && !label.startsWith('ref_carSpot') && !CAR_REFERENCE_KEYS.includes(label as typeof CAR_REFERENCE_KEYS[number]) && !isMenuReference(label)
                        );

                        return (
                          <>
                            {/* 愛車参照キーがある場合：MyGarageから選択ボタンを表示 */}
                            {carRefLabels.length > 0 && !hasPhoneUser && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-cyan-300">愛車参照:</h4>
                                <button
                                  onClick={handleMyGarageSelect}
                                  className="w-full bg-gray-700 hover:bg-gray-600 text-cyan-400 hover:text-cyan-300 font-medium py-2 px-3 rounded-md transition duration-150 ease-in-out flex items-center justify-center text-xs"
                                >
                                  <CameraIcon className="w-4 h-4 mr-1" />
                                  MyGarageから選択
                                </button>
                                {selectedCar && (
                                  <div className="text-xs text-cyan-400 text-center">
                                    選択中: {formatCarName(selectedCar)}
                                  </div>
                                )}
                                {/* 愛車参照画像の表示（横並び） */}
                                <div className="flex flex-wrap gap-2">
                                  {carRefLabels.map((label, index) => {
                                    const imageUrl = getCarReferenceImageUrl(label);
                                    const textValue = getCarReferenceText(label);
                                    const displayName = getCarReferenceDisplayName(label);
                                    const isTextRef = label === 'car_numbertext' || label === 'car_nickname';

                                    return (
                                      <div key={`car-ref-${index}`} className="flex-shrink-0" style={{ width: 'calc(50% - 4px)' }}>
                                        <label className="block text-[10px] font-medium text-cyan-400 mb-0.5 truncate" title={displayName}>
                                          {displayName}
                                        </label>
                                        {isTextRef ? (
                                          // テキスト参照の場合
                                          <div className="w-full p-1.5 bg-gray-700 rounded border border-cyan-500/50 h-16 flex items-center">
                                            {textValue ? (
                                              <span className="text-cyan-300 text-xs truncate">{textValue}</span>
                                            ) : (
                                              <span className="text-yellow-400 text-[10px]">
                                                {selectedCar ? '未登録' : '愛車を選択'}
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          // 画像参照の場合
                                          imageUrl ? (
                                            <div className="relative w-full h-16 bg-gray-700 rounded overflow-hidden border border-cyan-500/50">
                                              <img
                                                src={imageUrl}
                                                alt={displayName}
                                                className="w-full h-full object-contain"
                                              />
                                            </div>
                                          ) : (
                                            <div className="w-full h-16 bg-gray-700 rounded border border-dashed border-yellow-500/50 flex items-center justify-center">
                                              <span className="text-yellow-400 text-[10px] text-center px-1">
                                                {selectedCar ? '未登録' : '愛車を選択'}
                                              </span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* カータレント等の表示画像（ネタバレ防止用） */}
                            {displayOnlyRefs.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-purple-300">タレント:</h4>
                                {displayOnlyRefs.map((ref, index) => (
                                  <div key={`display-${index}`} className="space-y-1">
                                    <label className="block text-xs font-medium text-purple-400">
                                      {ref.displayName}
                                    </label>
                                    <div className="relative w-full h-32 bg-gray-700 rounded-lg overflow-hidden border-2 border-purple-500/50">
                                      <img
                                        src={ref.displayImageUrl!}
                                        alt={ref.displayName}
                                        className="w-full h-full object-contain"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* メニュー固定参照画像（自動適用、アップロード不要） */}
                            {menuRefLabels.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-orange-300">メニュー固定参照:</h4>
                                <div className="flex flex-wrap gap-2">
                                  {menuRefLabels.map((label, index) => {
                                    const imageUrl = getMenuRefImageUrl(label);
                                    const displayLabel = getMenuRefLabel(label);

                                    return (
                                      <div key={`menu-ref-${index}`} className="flex-shrink-0" style={{ width: 'calc(50% - 4px)' }}>
                                        <label className="block text-[10px] font-medium text-orange-400 mb-0.5 truncate" title={displayLabel}>
                                          {displayLabel}
                                        </label>
                                        {imageUrl ? (
                                          <div className="relative w-full h-16 bg-gray-700 rounded overflow-hidden border border-orange-500/50">
                                            <img
                                              src={imageUrl}
                                              alt={displayLabel}
                                              className="w-full h-full object-contain"
                                            />
                                          </div>
                                        ) : (
                                          <div className="w-full h-16 bg-gray-700 rounded border border-dashed border-red-500/50 flex items-center justify-center">
                                            <span className="text-red-400 text-[10px] text-center px-1">
                                              画像未設定
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1">
                                  ※ メニュー固定の参照画像は自動適用されます
                                </p>
                              </div>
                            )}

                            {/* 通常の参照画像 */}
                            {normalRefLabels.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-indigo-300">参照画像:</h4>
                                {normalRefLabels.map((label, index) => {
                                  const isAutoRef = label.startsWith('ref_');
                                  const originalIndex = refLabels.indexOf(label);
                                  const refFile = formData.referenceImages?.[originalIndex] || null;
                                  const refImageUrl = isAutoRef ? getImageUrlFromRefKey(label) : null;
                                  const displayName = isAutoRef ? getDisplayNameFromRefKey(label) : label;

                                  return (
                                    <div key={index}>
                                      {isAutoRef ? (
                                        // 参照設定キーの場合：プレビュー表示のみ（編集不可）
                                        <div className="space-y-1">
                                          <label className="block text-xs font-medium text-green-400">
                                            {displayName}
                                          </label>
                                          {refFile || refImageUrl ? (
                                            <div className="relative w-full h-32 bg-gray-700 rounded-lg overflow-hidden border-2 border-green-500/50">
                                              <img
                                                src={refFile ? URL.createObjectURL(refFile) : refImageUrl!}
                                                alt={displayName}
                                                className="w-full h-full object-contain"
                                              />
                                            </div>
                                          ) : (
                                            <div className="w-full h-32 bg-gray-700 rounded-lg border-2 border-dashed border-yellow-500/50 flex items-center justify-center">
                                              <span className="text-yellow-400 text-xs text-center px-2">
                                                参照設定に画像が登録されていません。<br />
                                                設定画面から登録してください。
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        // フリー入力ラベルの場合：従来のImageUpload
                                        <ImageUpload
                                          onImageSelect={(file) => {
                                            setFormData((prev) => {
                                              const newRefImages = [...(prev.referenceImages || [])];
                                              // 配列を必要な長さまで拡張
                                              while (newRefImages.length <= originalIndex) {
                                                newRefImages.push(null);
                                              }
                                              newRefImages[originalIndex] = file;
                                              return {
                                                ...prev,
                                                referenceImages: newRefImages,
                                              };
                                            });
                                          }}
                                          label={label}
                                          uploadedFile={refFile}
                                          showDeleteButton={false}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <>
                      <ImageUpload
                        ref={imageUploadRef}
                        onImageSelect={handleImageSelectedByUploader}
                        label="基準にする愛車写真"
                        uploadedFile={formData.image}
                        showDeleteButton={false}
                      />
                      {/* MyGarage認証ユーザーのみに表示 - ファイルアップロードボタンの横に配置 */}
                      {!hasPhoneUser && (
                        <div className="mt-2">
                          <button
                            onClick={handleMyGarageSelect}
                            className="w-full bg-gray-700 hover:bg-gray-600 text-indigo-400 hover:text-indigo-300 font-medium py-2 px-3 rounded-md transition duration-150 ease-in-out flex items-center justify-center text-xs"
                          >
                            <CameraIcon className="w-4 h-4 mr-1" />
                            MyGarageから選択
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {formData.inputType === 'prompt' && (
                <div>
                  <label
                    htmlFor="additionalPromptForMyCar"
                    className="block text-xs lg:text-sm font-medium text-gray-300 mb-1"
                  >
                    主な被写体の説明 (テキスト入力ベース時)
                  </label>
                  <textarea
                    id="additionalPromptForMyCar"
                    value={formData.additionalPromptForMyCar}
                    onChange={(e) =>
                      handleAdditionalPromptForMyCar(e.target.value)
                    }
                    rows={2}
                    placeholder="例: 赤い流線型のスポーツカー、未来的なデザイン"
                    className="w-full p-2 text-xs lg:text-sm bg-gray-700 border border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-gray-200 placeholder-gray-500"
                    maxLength={500}
                  />
                </div>
              )}
            </div>

            <div className="pt-2">
              <h3 className="text-md font-semibold text-indigo-400 mb-2 mt-2 flex items-center">
                <AdjustmentsHorizontalIcon className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
                追加共通オプション
              </h3>
              {/* 画像比率 - 両方の入力タイプで表示 */}
              <div>
                <label
                  htmlFor="aspectRatio"
                  className="block text-xs lg:text-sm font-medium text-gray-300 mb-1"
                >
                  画像比率
                </label>
                <select
                  id="aspectRatio"
                  value={formData.aspectRatio}
                  onChange={(e) =>
                    handleAspectRatio(e.target.value as AspectRatio)
                  }
                  className="w-full p-2 text-xs lg:text-sm bg-gray-700 border border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-gray-200"
                >
                  {/* 画像アップロードの時は「元画像のまま」を追加 */}
                  {formData.inputType === 'upload' && (
                    <option value="">元画像のまま</option>
                  )}
                  {AVAILABLE_ASPECT_RATIOS.map((ar) => (
                    <option key={ar} value={ar}>
                      {ar}
                    </option>
                  ))}
                </select>
              </div>
              {/* 管理者のみ追加プロンプトフォームを表示 */}
              {currentUser?.isAdmin && (
                <div className="mt-3">
                  <label
                    htmlFor="additionalPromptForOthers"
                    className="block text-xs lg:text-sm font-medium text-gray-300 mb-1"
                  >
                    追加プロンプト (全体に効果、任意)
                  </label>
                  <input
                    type="text"
                    id="additionalPromptForOthers"
                    value={formData.additionalPromptForOthers}
                    onChange={(e) =>
                      handleAdditionalPromptForOthers(e.target.value)
                    }
                    placeholder="例: 超リアル、映画風ライト、魚眼レンズ"
                    className="w-full p-2 text-xs lg:text-sm bg-gray-700 border border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-gray-200 placeholder-gray-500"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {/* HeyGen: ユーザー追加テキスト入力 */}
        {heygenAllowUserScript && (
          <div className="mt-4">
            <label className="text-sm font-semibold text-teal-400 block mb-1">
              追加テキスト（最大{heygenMaxLen}文字）
            </label>
            <textarea
              value={formData.script || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, script: e.target.value.slice(0, heygenMaxLen) }))}
              rows={3}
              maxLength={heygenMaxLen}
              placeholder="喋らせたい内容を入力（固定台本の後に追加されます）"
              className="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-gray-200 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{(formData.script || '').length} / {heygenMaxLen}</p>
          </div>
        )}
        {/* HeyGen(音源): 音声アップロード */}
        {isHeyGenAudio && (
          <div className="mt-4">
            <label className="text-sm font-semibold text-teal-400 block mb-1">音声ファイル（mp3 / wav・必須）</label>
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => setFormData((prev) => ({ ...prev, audioFile: e.target.files?.[0] ?? null }))}
              className="w-full text-sm text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-teal-600 file:text-white"
            />
            {formData.audioFile && (
              <p className="text-xs text-gray-400 mt-1">選択中: {formData.audioFile.name}</p>
            )}
          </div>
        )}
        {/* 動画: 人物参照の表示（Renoiseアセット + Sousaku系人物ラベル） */}
        {(() => {
          const eng = formData.menu?.engine || '';
          const isI2v = ['seedance2_sousaku_i2v', 'seedance25_sousaku_i2v', 'hotgen_video_v4_pro', 'minimax_h3_max_turbo', 'runway_gen45', 'gemini_omni_flash', 'topview_t1_i2v', 'seedance25_topview_i2v', 'seedance2_topview_i2v'].includes(eng);
          const isRenoise = ['seedance2', 'seedance2_fast', 'seedance25', 'kling3_omni'].includes(eng);
          // 系統に応じて使う方だけ表示（i2v型は人物参照なし / Renoise系=アセット / Sousaku refs系=人物ラベル）
          const personAssets = (isRenoise && !isI2v) ? ((formData.menu?.videoPersonAssets || formData.menu?.video_person_assets || []) as any[]) : [];
          const personLabels = (!isRenoise && !isI2v) ? ((formData.menu?.videoPersonRefLabels || formData.menu?.video_person_ref_labels || []) as string[]) : [];
          const menuRefs = (formData.menu?.menuReferenceImages || formData.menu?.menu_reference_images || []) as { label: string; url: string }[];

          // 表示エントリを統合
          const entries: { name: string; thumb?: string }[] = [];
          for (const a of personAssets) {
            entries.push({ name: a.display_name ?? a.displayName ?? a.name, thumb: a.thumbnail_url ?? a.thumbnailUrl });
          }
          for (const label of personLabels) {
            const menuRefMatch = label.match(/^menu_ref_(\d+)$/);
            if (menuRefMatch) {
              const ref = menuRefs[parseInt(menuRefMatch[1], 10) - 1];
              if (ref) entries.push({ name: ref.label, thumb: ref.url });
              continue;
            }
            // カータレント・ユーザー参照は既存ヘルパーで表示名/画像を解決
            entries.push({
              name: getDisplayNameFromRefKey(label),
              thumb: getDisplayImageUrlFromRefKey(label) || undefined,
            });
          }
          if (entries.length === 0) return null;
          return (
            <div className="mt-4">
              <p className="text-sm font-semibold text-pink-400 mb-1">参照画像（人物）:</p>
              <div className="flex flex-wrap gap-2">
                {entries.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-700 border border-pink-500/50 rounded px-2 py-1">
                    {e.thumb && (
                      <img src={e.thumb} alt={e.name} className="w-10 h-10 object-cover rounded" />
                    )}
                    <span className="text-sm text-gray-200">{e.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">※ 自動適用されます</p>
            </div>
          );
        })()}
      </div>

      <div className="mt-auto pt-4 border-t border-gray-700">
        <div className="mb-3">
          <p className="text-xs lg:text-sm text-gray-400">
            生成コスト:{' '}
            <span className="font-semibold text-indigo-400">
              {selectedMenuCredit} クレジット
            </span>
          </p>
          <p className="text-xs lg:text-sm text-gray-400">
            保有クレジット:{' '}
            <span className="font-semibold text-green-400">
              {credits} クレジット
            </span>
          </p>
          {!canGenerate && credits < selectedMenuCredit && (
            <p className="text-xs lg:text-sm text-red-400 mt-1">
              クレジットが不足しています。
            </p>
          )}
          {!canGenerate && credits >= selectedMenuCredit && !formData.menu && (
            <p className="text-xs lg:text-sm text-yellow-400 mt-1">
              カテゴリとメニューを選択してください。
            </p>
          )}
          {!canGenerate &&
            credits >= selectedMenuCredit &&
            formData.menu &&
            formData.inputType === 'upload' &&
            !isAdvancedEditEngine &&
            !formData.image && (
              <p className="text-xs lg:text-sm text-yellow-400 mt-1">
                基準画像をアップロードまたは選択してください。
              </p>
            )}
          {!canGenerate &&
            credits >= selectedMenuCredit &&
            formData.menu &&
            formData.inputType === 'upload' &&
            isAdvancedEditEngine &&
            carReferenceRequired && (
              <p className="text-xs lg:text-sm text-yellow-400 mt-1">
                愛車を選択してください。
              </p>
            )}
          {!canGenerate &&
            credits >= selectedMenuCredit &&
            formData.menu &&
            formData.inputType === 'upload' &&
            isAdvancedEditEngine &&
            !advancedImagesValid &&
            !carReferenceRequired && (
              <p className="text-xs lg:text-sm text-yellow-400 mt-1">
                必要な画像をすべてアップロードしてください。
              </p>
            )}
          {!canGenerate &&
            credits >= selectedMenuCredit &&
            formData.menu &&
            formData.inputType === 'prompt' &&
            (!formData.additionalPromptForMyCar?.trim() ||
              !(
                formData.menu.promptVariables &&
                0 < formData.menu.promptVariables.length &&
                formData.menu.promptVariables.some(
                  (v) =>
                    formData.promptVariables.find(
                      (inputVar) => inputVar.key === v.key,
                    ) &&
                    formData.promptVariables.some(
                      (inputVar) => inputVar.value.trim() === '',
                    ),
                )
              )) && (
              <p className="text-xs lg:text-sm text-yellow-400 mt-1">
                「主な被写体の説明」またはメニュー固有の「追加入力」を入力してください。
              </p>
            )}
        </div>
        <button
          onClick={() => handleGeneration()}
          disabled={isGenerating || !canGenerate}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white font-bold py-3.5 px-4 rounded-lg shadow-md transition duration-150 ease-in-out flex items-center justify-center text-sm lg:text-base"
          aria-label={`画像を生成 (${selectedMenuCredit} クレジット)`}
        >
          <BoltIcon className="w-5 h-5 mr-2" />
          {isGenerating
            ? '生成中...'
            : `画像を生成 (${selectedMenuCredit} クレジット)`}
        </button>
        <p className="text-xs text-gray-500 text-center mt-2">
          画像生成の実行は
          <button
            onClick={() => setShowTermsModal(true)}
            className="text-indigo-400 hover:text-indigo-300 underline"
          >
            利用規約
          </button>
          に同意したものとみなします
        </p>
      </div>
      {formData.menu && showSampleModal && (
        <SampleImagesModal
          isOpen={showSampleModal}
          onClose={() => setShowSampleModal(false)}
          sourceUrl={formData.menu.sampleInputImageUrl}
          generatedUrl={formData.menu.sampleResultImageUrl}
          menuName={formData.menu.name}
        />
      )}
      
      {/* 愛車選択モーダル */}
      {isCarSelectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">愛車を選択</h3>
              <button
                onClick={() => setIsCarSelectModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {carList.length === 0 ? (
                <p className="text-gray-400 text-center py-8">愛車が登録されていません</p>
              ) : (
                <div className="space-y-3">
                  {carList.map((car) => (
                    <button
                      key={car.id}
                      onClick={() => handleCarSelect(car)}
                      className={`w-full p-3 rounded-lg border transition-all duration-200 flex items-center space-x-3 ${
                        selectedCar?.id === car.id
                          ? 'border-indigo-500 bg-indigo-600/20'
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={car.car_image_url}
                          alt={formatCarName(car)}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder-car.png';
                          }}
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium text-white">
                          {formatCarName(car)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {car.car_brand_ja} {car.car_model_ja}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* 利用規約モーダル */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
};
